import {useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {fetchDropoutDictionary} from 'api';
import {SelectOption} from 'components/ui-kit/Select';

const toFallbackLabel = (value: string) =>
    value
        .toLowerCase()
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');

const mapDictionaryValuesToOptions = (
    values: string[],
    scope: string,
    t: (value: string, options?: {defaultValue: string}) => string,
): SelectOption<string>[] =>
    values.map((value) => {
        const path = `${scope}.${value}`;
        const fallbackLabel = toFallbackLabel(value);

        return {
            label: t(path, {defaultValue: fallbackLabel}),
            value,
        };
    });

export const useDropoutDictionaryOptions = () => {
    const {t} = useTranslation();
    const [reasons, setReasons] = useState<string[]>([]);
    const [salesChannels, setSalesChannels] = useState<string[]>([]);

    useEffect(() => {
        let canceled = false;

        fetchDropoutDictionary()
            .then((data) => {
                if (!canceled) {
                    setReasons(data.reasons ?? []);
                    setSalesChannels(data.salesChannels ?? []);
                }
            })
            .catch((error) => {
                console.error(error);
                if (!canceled) {
                    setReasons([]);
                    setSalesChannels([]);
                }
            });

        return () => {
            canceled = true;
        };
    }, []);

    return useMemo(
        () => ({
            reasons: mapDictionaryValuesToOptions(
                reasons,
                'dictionaries.documents.DropOut.reasons',
                t,
            ),
            salesChannels: mapDictionaryValuesToOptions(
                salesChannels,
                'dictionaries.documents.DropOut.salesChannels',
                t,
            ),
        }),
        [reasons, salesChannels, t],
    );
};
